import React from 'react';

import { ProfileImage } from '@xingternal/profile-image';
import { StarRating } from '@xingternal/star-rating';
import { BodyCopy, Headline } from '@xingternal/typography';

import { OptimisticPublicationDate } from '@/components/_common/optimistic-publication-date';
import { useBookmark, useBreakpoints } from '@/hooks';
import { LineClampOne, LineClampTwo } from '@/styles/line-clamp';

import { selectJobData } from '../../job-teaser-helpers';
import { ActionButtons } from '../action-buttons';
import { JobTeaserFacts, MatchingHeader } from '../facts';
import { JobTeaserProps } from '../types';

import { SGapCol, XxsGapCol } from '../shared.styles';
import * as S from './job-teaser-card.styles';

type Props = JobTeaserProps & { showRating?: boolean };

export const JobTeaserCard: React.FC<Props> = ({
  job,
  isNew,
  url,
  matchingHighlights,
  className,
  onClick,
  onContextMenu,
  onAuxClick,
  onMouseDown,
  onMouseLeave,
  onDelete,
  hideBookmark,
  nwtTrackingOptions,
  'data-testid': dataTestId,
  'data-cy': testCyId,
  headingLevel = 'h3',
  showRating = false,
}) => {
  const { ratingAverage, date, title, location, companyName, companyLogos, isBookmarked } = selectJobData(job);

  const { isMobile } = useBreakpoints();

  const toggleBookmark = useBookmark({
    jobId: job.id,
    isBookmarked,
    nwtTrackingOptions,
  });

  const onBookmark = hideBookmark ? undefined : toggleBookmark;

  return (
    <S.Article className={className}>
      <S.Card
        to={url}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onAuxClick={onAuxClick}
        linkProps={{ onMouseDown, onMouseLeave }}
        data-testid={dataTestId}
        data-cy={testCyId}
      >
        <S.HeaderContainer>
          <S.TopRow>
            <ProfileImage
              type="company"
              profileName={companyName}
              size="small"
              src={companyLogos.x1 ?? ''}
              title={null}
            />
            <MatchingHeader matchingHighlights={matchingHighlights} />
          </S.TopRow>

          <S.Details>
            <XxsGapCol>
              <LineClampTwo
                as={Headline}
                size="medium"
                forwardAs={headingLevel}
                data-cy="job-teaser-card-title"
                noMargin
              >
                {title}
              </LineClampTwo>

              {location?.city ? (
                <LineClampOne as={BodyCopy} size="medium" lineHeight="dense" noMargin>
                  {location.city}
                </LineClampOne>
              ) : null}

              <LineClampTwo as={BodyCopy} size="medium" lineHeight="dense" noMargin>
                {companyName}
              </LineClampTwo>
              {showRating ? <StarRating rating={ratingAverage} /> : null}
            </XxsGapCol>

            <JobTeaserFacts job={job} matchingHighlights={matchingHighlights} />
          </S.Details>
        </S.HeaderContainer>
        <SGapCol>
          {date ? (
            <S.Date size="small" lineHeight="dense">
              {isNew ? <S.Notification /> : null}
              <OptimisticPublicationDate date={date} />
            </S.Date>
          ) : null}

          {onDelete || onBookmark ? (
            <ActionButtons
              size={isMobile ? 'medium' : 'small'}
              isBookmarked={isBookmarked}
              onBookmark={onBookmark}
              onDelete={onDelete}
              noMargin
              bottom
            />
          ) : null}
        </SGapCol>
      </S.Card>
    </S.Article>
  );
};
